import { useEffect, useState } from 'react';
import { Typography } from '@mui/material';
import { Toolbar } from '@mui/x-data-grid';

import { getVehicleTypes } from '@/generated/client/targetattributes/targetattributes';
import { VehicleTypeDto, VehicleTypeDtoVehicleCategory } from '@/generated/model';
import { useTranslation } from 'react-i18next';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { SearchComponent } from '@/components/common/Search';
import SearchNoResult from '@/shared/ui/no-result-display/SearchNoResult';
import { ToolbarSortItem } from '@/components/common/ToolbarSortItem';

type MobileVehicleTypeSelectorProps = {
  selectedId?: string;
  onSelect: (id: string) => void;
};

export default function MobileVehicleTypeSelector({ selectedId, onSelect }: MobileVehicleTypeSelectorProps) {
  const { t } = useTranslation();
  const [vehicleTypes, setVehicleTypes] = useState<VehicleTypeDto[] | undefined>();
  const [searchText, setSearchText] = useState<string | undefined>();
  const [originalRows, setOriginalRows] = useState<VehicleTypeDto[]>([]);

  async function loadPossibleValues() {
    const newValues = (await getVehicleTypes()).data;
    setVehicleTypes(newValues);
    setOriginalRows(newValues);
  }

  useEffect(() => {
    loadPossibleValues();
  }, []);

  useEffect(() => {
    if (!searchText) {
      setVehicleTypes(originalRows);
      return;
    }
    const lower = searchText.toLowerCase();
    const filtered = originalRows.filter((row) =>
      Object.values(row).some((v) => String(v).toLowerCase().includes(lower))
    );
    setVehicleTypes(filtered);
  }, [searchText, originalRows]);

  if (!vehicleTypes) {
    return <span className="target-attribute-value disabled">{t('targetAttributes.pleaseWait')}</span>;
  }

  const columns = [
    {
      field: 'brand',
      headerName: t('targetAttributes.vehicleBrand'),
      resizable: false,
      flex: 1,
    },
    {
      field: 'type',
      headerName: t('targetAttributes.vehicleType'),
      resizable: false,
      flex: 1,
    },
    {
      field: 'vehicleCategory',
      headerName: t('targetAttributes.vehicleCategory'),
      resizable: false,
      flex: 1,
      valueFormatter: (value?: VehicleTypeDtoVehicleCategory) => {
        if (!value) return '';
        return t('targetAttributes.category.' + value);
      },
    },
  ];

  function CustomToolbar() {
    return (
      <Toolbar style={{ boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.25)', zIndex: 10000 }}>
        <Typography variant="body2" pl={'8px'}>
          {!searchText ? `(${originalRows.length})` : `(${originalRows.length}/${vehicleTypes?.length})`}
        </Typography>
        <ToolbarSortItem listView={true} title={t('general.sortBy')} />
      </Toolbar>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <SearchComponent
        searchText={searchText}
        setSearchText={setSearchText}
        showHistory={false}
        placeholder={t('targetAttributes.searchPlaceHolder')}
      />
      <DataGridPro
        className="targetattributes-vehicletype-grid"
        rows={vehicleTypes}
        columns={columns}
        getRowId={(row) => String(row.id)}
        rowSelectionModel={{ type: 'include', ids: new Set(selectedId ? [selectedId] : []) }}
        onRowClick={({ row }) => onSelect(String(row.id))}
        hideFooter
        listView
        showToolbar
        slots={{
          toolbar: CustomToolbar,
          noRowsOverlay: () => <SearchNoResult searchText={searchText} />,
        }}
        sx={{
          '& .MuiDataGrid-row': {
            pl: '10px',
          },
          '& .MuiDataGrid-cell': {
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            lineHeight: '1',
          },
        }}
      />
    </div>
  );
}
